/**
 * Main Layout Component
 * Sidebar navigation and header — responsive (drawer on mobile)
 */

import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Button,
  Typography,
  Space,
  Drawer,
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  ProjectOutlined,
  DollarOutlined,
  FileTextOutlined,
  BarChartOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CalendarOutlined,
  TeamOutlined,
  CalculatorOutlined,
  MenuOutlined,
  EditOutlined,
  QrcodeOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../store/authStore';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const MOBILE_BREAKPOINT = 768;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setDrawerOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close drawer on navigation
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/clients',
      icon: <UserOutlined />,
      label: 'Clients',
    },
    {
      key: '/projects',
      icon: <ProjectOutlined />,
      label: 'Projects',
    },
    {
      key: '/estimator-v2',
      icon: <CalculatorOutlined />,
      label: 'Estimator',
    },
    {
      key: 'billing',
      icon: <DollarOutlined />,
      label: 'Billing',
      children: [
        {
          key: '/billing/estimates',
          label: 'Estimates',
        },
        {
          key: '/billing/invoices',
          label: 'Invoices',
        },
      ],
    },
    {
      key: '/esign',
      icon: <EditOutlined />,
      label: 'Signatures',
    },
    {
      key: 'schedule',
      icon: <CalendarOutlined />,
      label: 'Schedule',
      children: [
        {
          key: '/schedule',
          label: 'Timeline',
        },
        {
          key: '/calendar',
          label: 'Calendar',
        },
      ],
    },
    {
      key: '/appointments',
      icon: <CalendarOutlined />,
      label: 'Appointments',
    },
    {
      key: 'financials',
      icon: <DollarOutlined />,
      label: 'Financials',
      children: [
        {
          key: '/financials/expenses',
          label: 'Expenses',
        },
        {
          key: '/financials/direct-payments',
          label: 'Direct Payments',
        },
        {
          key: '/financials/owner-draws',
          label: "Owner's Draws",
        },
        {
          key: '/financials/profit',
          label: 'Profit Distribution',
        },
        {
          key: '/financials/business-goals',
          label: 'Business Goals',
        },
        {
          key: '/financials/tax-payments',
          label: 'Tax Payments',
        },
      ],
    },
    {
      key: 'tax',
      icon: <FileTextOutlined />,
      label: 'Tax Center',
      children: [
        {
          key: '/tax/dashboard',
          label: 'Overview',
        },
        {
          key: '/tax/expenses',
          label: 'Expenses',
        },
        {
          key: '/tax/mileage',
          label: 'Mileage',
        },
        {
          key: '/tax/home-office',
          label: 'Home Office',
        },
        {
          key: '/tax/depreciation',
          label: 'Depreciation',
        },
        {
          key: '/tax/per-diem',
          label: 'Per Diem',
        },
      ],
    },
    {
      key: '/field',
      icon: <ToolOutlined />,
      label: 'Field',
      children: [
        { key: '/field', label: 'Today Board' },
        { key: '/field/planning', label: 'Planning' },
        { key: '/field/workers', label: 'Workers' },
        { key: '/field/sites', label: 'Sites' },
        { key: '/field/alerts', label: 'Alerts' },
        { key: '/field/payroll', label: 'Payroll' },
      ],
    },
    {
      key: '/payroll',
      icon: <TeamOutlined />,
      label: 'Payroll',
    },
    {
      key: '/licensing',
      icon: <SafetyCertificateOutlined />,
      label: 'Licensing',
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: 'Reports',
    },
    {
      key: '/qr',
      icon: <QrcodeOutlined />,
      label: 'QR & Cards',
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => {
        navigate('/settings/profile');
      },
    },
    {
      key: 'company',
      icon: <SettingOutlined />,
      label: 'Company Settings',
      onClick: () => {
        navigate('/settings/company');
      },
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: async () => {
        await logout();
        navigate('/login');
      },
    },
  ];

  const handleMenuClick = (e: { key: string }) => {
    navigate(e.key);
  };

  // Compute selected + open keys by finding the best matching menu item
  const computeMenuKeys = () => {
    const path = location.pathname;
    const allKeys: string[] = [];
    const parentMap: Record<string, string> = {};
    menuItems.forEach((item) => {
      if (item.children) {
        item.children.forEach((child: any) => {
          allKeys.push(child.key);
          parentMap[child.key] = item.key;
        });
      } else {
        allKeys.push(item.key);
      }
    });
    // Exact match first, then longest prefix
    let bestKey = path;
    if (!allKeys.includes(path)) {
      const matches = allKeys
        .filter((k) => k !== '/' && path.startsWith(k))
        .sort((a, b) => b.length - a.length);
      bestKey = matches[0] || path;
    }
    return { selected: [bestKey], open: parentMap[bestKey] ? [parentMap[bestKey]] : [] };
  };
  const { selected: selectedKeys, open: openKeys } = computeMenuKeys();

  const sidebarContent = (
    <>
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Typography.Title level={4} style={{ margin: 0, color: '#1890ff' }}>
          {isMobile || !collapsed ? 'GCMM Platform' : 'GC'}
        </Typography.Title>
      </div>
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        defaultOpenKeys={openKeys}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ borderRight: 0 }}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={250}
          theme="light"
          style={{
            boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={260}
          bodyStyle={{ padding: 0 }}
          headerStyle={{ display: 'none' }}
        >
          {sidebarContent}
        </Drawer>
      )}

      <Layout>
        <Header
          style={{
            padding: '0 16px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            zIndex: 1,
          }}
        >
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setDrawerOpen(true)}
              style={{ fontSize: 18 }}
            />
          ) : (
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 16 }}
            />
          )}

          <Space>
            <Text strong style={{ fontSize: isMobile ? 12 : 14 }}>
              {user?.first_name} {user?.last_name}
            </Text>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar
                style={{ cursor: 'pointer', backgroundColor: '#1890ff' }}
                icon={<UserOutlined />}
              >
                {user?.first_name?.[0]}
              </Avatar>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: isMobile ? '12px' : '24px',
            padding: isMobile ? 12 : 24,
            minHeight: 280,
            background: '#f0f2f5',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
